/**
 * Page Moteur de Règles Autonomie — US-113
 * Lecture : ADMIN + CADRE / Modification : ADMIN uniquement
 */

import { useEffect, useState } from 'react';
import { apiClient } from '@/shared/services/apiClient';
import { API_ENDPOINTS } from '@/config/api';
import { useAuth } from '@/features/auth/contexts/AuthContext';

interface Policy {
  acte_type: string;
  level: string;
  service: string | null;
  updated_by: string | null;
  updated_at: string | null;
}

const LEVEL_LABELS: Record<string, string> = {
  assiste: 'Assisté',
  semi_auto: 'Semi-auto',
  auto_supervise: 'Auto-supervisé',
};

const LEVEL_BADGE: Record<string, string> = {
  assiste: 'bg-success',
  semi_auto: 'bg-warning text-dark',
  auto_supervise: 'bg-danger',
};

const ACTE_LABELS: Record<string, string> = {
  tracabilite_medicamenteuse: 'Traçabilité médicamenteuse',
  transmission_dar: 'Transmission DAR',
  observation_libre: 'Observation libre',
  consultation_dossier: 'Consultation dossier',
  commande_systeme: 'Commande système',
};

const LEVELS = ['assiste', 'semi_auto', 'auto_supervise'];

export function PolicyPage() {
  const { user } = useAuth();
  const isAdmin = user?.profil === 'admin';

  const [policies, setPolicies] = useState<Policy[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  useEffect(() => {
    apiClient.get<{ items: Policy[]; count: number }>(API_ENDPOINTS.policy)
      .then((r) => setPolicies(r.items))
      .catch(() => setError('Erreur de chargement des politiques'))
      .finally(() => setLoading(false));
  }, []);

  const handleLevelChange = async (acteType: string, newLevel: string) => {
    setSaving(acteType);
    setError(null);
    try {
      await apiClient.patch(API_ENDPOINTS.policyByType(acteType), { level: newLevel });
      setPolicies((prev) =>
        prev.map((p) => p.acte_type === acteType ? { ...p, level: newLevel } : p)
      );
      setSuccessMsg(`Politique "${ACTE_LABELS[acteType]}" mise à jour → ${LEVEL_LABELS[newLevel]}`);
      setTimeout(() => setSuccessMsg(null), 3000);
    } catch {
      setError('Erreur lors de la mise à jour');
    } finally {
      setSaving(null);
    }
  };

  return (
    <div>
      <div className="d-flex align-items-center gap-3 mb-4">
        <h2 className="fw-bold mb-0">
          <i className="bi bi-shield-check me-2 text-primary"></i>
          Moteur de Règles Autonomie
        </h2>
        {!isAdmin && (
          <span className="badge bg-secondary">Lecture seule</span>
        )}
      </div>

      <p className="text-muted mb-4">
        Définit le niveau d'autonomie des agents pour chaque type d'acte clinique.
        Trois niveaux : <strong>Assisté</strong> (validation humaine systématique),
        <strong>Semi-auto</strong> (HITL sur seuil), <strong>Auto-supervisé</strong> (audit post-hoc).
      </p>

      {error && (
        <div className="alert alert-danger d-flex align-items-center gap-2">
          <i className="bi bi-exclamation-circle"></i>
          {error}
        </div>
      )}

      {successMsg && (
        <div className="alert alert-success d-flex align-items-center gap-2">
          <i className="bi bi-check-circle"></i>
          {successMsg}
        </div>
      )}

      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" />
        </div>
      ) : (
        <div className="row g-3">
          {policies.map((policy) => (
            <div key={policy.acte_type} className="col-12 col-md-6">
              <div className="card h-100">
                <div className="card-body">
                  <div className="d-flex align-items-start justify-content-between gap-2 mb-3">
                    <div>
                      <h6 className="fw-semibold mb-1">
                        {ACTE_LABELS[policy.acte_type] ?? policy.acte_type}
                      </h6>
                      <code className="text-muted" style={{ fontSize: '0.75rem' }}>
                        {policy.acte_type}
                      </code>
                    </div>
                    <span className={`badge ${LEVEL_BADGE[policy.level] ?? 'bg-secondary'}`}>
                      {LEVEL_LABELS[policy.level] ?? policy.level}
                    </span>
                  </div>

                  {isAdmin ? (
                    <div className="d-flex align-items-center gap-2">
                      <select
                        className="form-select form-select-sm"
                        value={policy.level}
                        disabled={saving === policy.acte_type}
                        onChange={(e) => handleLevelChange(policy.acte_type, e.target.value)}
                      >
                        {LEVELS.map((l) => (
                          <option key={l} value={l}>{LEVEL_LABELS[l]}</option>
                        ))}
                      </select>
                      {saving === policy.acte_type && (
                        <div className="spinner-border spinner-border-sm text-primary" />
                      )}
                    </div>
                  ) : (
                    <p className="text-muted small mb-0">
                      Niveau actuel : <strong>{LEVEL_LABELS[policy.level] ?? policy.level}</strong>
                    </p>
                  )}

                  {policy.updated_at && (
                    <small className="text-muted d-block mt-2">
                      Modifié le {new Date(policy.updated_at).toLocaleString('fr-FR')}
                    </small>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
